import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { useSelector } from 'react-redux';
import { Button, Flex, Segmented, Tag } from 'antd';
import moment from 'moment-timezone';
import FadeIn from '../FadeIn';
import SubscriptionProduct from '../billing/SubscriptionProduct';
import { SubscriptionProductType, TAX_BEHAVIOUR } from '../../services/exports/Constants';
import Subscription from '../billing/Subscription';
import Agreement from '../billing/Agreement';
import {
  clampDiscount,
  discountLineItem,
  discountSubscription,
  isMobileAppMainSubscription,
} from '../../services/helpers/ContractDiscountHelpers';
import PaymentInfoStep from './PaymentInfoStep';
import useApiClient from '../../hooks/api/useApiClient';
import useHelpers from '../../hooks/context/useHelpers';
import TaxCalculator from '../../services/helpers/TaxCalculator';
import {
  useCreateSubscriptionMutation,
  useGetServiceContractQuery,
  useGetSubscriptionsQuery,
  useUpdateSubscriptionMutation,
} from '../../store/api-slices';
import usePermissions, { ManagerPermission } from '../../hooks/auth/usePermissions';

const steps = {
  select_products: 0,
  subscribe: 1,
};

export function SubscriptionWrapper({ renderSubscriptions }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:SubscriptionStep' });

  const { SubscriptionProductsManager, SubscriptionsManager } = useApiClient();
  const { can } = usePermissions();

  const { company } = useSelector((state) => state.currentCompany);
  const { data: subscriptions, isFetching } = useGetSubscriptionsQuery({
    companyId: company?.id,
    apiManager: SubscriptionsManager,
  });
  const [subscriptionProducts, setSubscriptionProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([can(ManagerPermission.ManageSubscriptions) && loadSubscriptionProducts()]).finally(() =>
      setLoading(false),
    );
  }, []);

  const loadSubscriptionProducts = async () => {
    const { success, data } = await SubscriptionProductsManager.get();

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    return setSubscriptionProducts(data.data);
  };

  return renderSubscriptions({
    loading: loading || isFetching,
    subscriptions: subscriptions?.data ?? [],
    subscriptionProducts,
  });
}

export const Mode = {
  preview: 'preview',
  edit: 'edit',
};

export const PlanTab = {
  actual: 'actual',
  legacy: 'legacy',
};

export default function SubscriptionStep({
  data,
  subscriptionProducts,
  mode = Mode.preview,
  filterOtherProducts,
  onEdit,
  onSave,
  moveForward,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:SubscriptionStep' });

  const ref = useRef(null);

  const { CurrentCompanyManager, SubscriptionsManager, ServiceContractsManager } = useApiClient();
  const { formatCurrency } = useHelpers();
  const [createSubscription, { isLoading: isCreateLoading }] = useCreateSubscriptionMutation();
  const [updateSubscription, { isLoading: isUpdateLoading }] = useUpdateSubscriptionMutation();
  // The annual-contract discount is granted on the service contract (set by a
  // master service provider). Here we only read it — when
  // `contract_discount_percentage` is a non-zero value the discount is reflected
  // in the displayed prices; the persisted line items stay undiscounted so the
  // backend recomputes the discount itself.
  const { data: serviceContract } = useGetServiceContractQuery(
    { apiManager: ServiceContractsManager },
    { refetchOnMountOrArgChange: true },
  );

  const { company } = useSelector((store) => store.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { merchant } = company;
  const { payment_options } = merchant;
  const hasPaymentOption = payment_options?.length > 0;

  const isLive = company.live_from !== null;
  const hasActiveTrial = isLive && data?.trial_ends_at && moment(data.trial_ends_at).isAfter(moment());

  const [agreementAccepted, setAgreementAccepted] = useState(false);

  const [activePlanTab, setActivePlanTab] = useState(PlanTab.actual);
  const [step, setStep] = useState(data && mode !== Mode.edit ? steps.subscribe : steps.select_products);
  const [isEdit, setIsEdit] = useState(mode === Mode.edit);
  const [billingMode, setBillingMode] = useState(
    data?.billing_mode ?? serviceProvider.default_subscription_billing_mode,
  );
  const [hasTrial, setHasTrial] = useState(hasActiveTrial || data?.trial_days > 0);
  const [trialDays, setTrialDays] = useState(
    hasActiveTrial && !!data ? moment(data.trial_ends_at).diff(moment(), 'days') + 1 : data?.trial_days,
  );

  const findSubscriptionItem = (product) => collect(data?.items ?? []).firstWhere('product.id', product.id);

  const [personalProducts, setPersonalProducts] = useState([]);

  const initialPersonalProducts = useMemo(
    () =>
      collect(subscriptionProducts)
        .map((product) => {
          const subscriptionItem = findSubscriptionItem(product);

          return subscriptionItem ? { ...product, price: subscriptionItem.price } : product;
        })
        .toArray(),
    [data, subscriptionProducts],
  );

  useEffect(() => {
    setPersonalProducts(initialPersonalProducts);
  }, [initialPersonalProducts]);

  const plans = useMemo(
    () => collect(personalProducts).where('type', SubscriptionProductType.Plan).toArray(),
    [personalProducts],
  );
  const legacyPlans = useMemo(
    () => collect(personalProducts).where('type', SubscriptionProductType.LegacyPlan).toArray(),
    [personalProducts],
  );
  const otherProducts = useMemo(
    () =>
      collect(personalProducts)
        .whereNotIn('id', [...collect(plans).pluck('id').toArray(), ...collect(legacyPlans).pluck('id').toArray()])
        .when(!!filterOtherProducts, (collection) => collection.filter(filterOtherProducts))
        .toArray(),
    [personalProducts, plans, legacyPlans, filterOtherProducts],
  );

  const [lineItems, setLineItems] = useState(
    collect(data?.items ?? [])
      .filter((item) => item.product.is_active && item.price.is_active)
      .toArray(),
  );
  // Apply the contract discount for display only when it's a non-zero value
  // (0/null → no discount). `lineItems` always holds the original (undiscounted)
  // values — those are what we persist, leaving the backend to recompute.
  const effectiveDiscount = serviceContract?.contract_discount_percentage
    ? clampDiscount(serviceContract.contract_discount_percentage)
    : 0;
  const discountedLineItems = useMemo(
    () =>
      collect(lineItems)
        .map((item) => discountLineItem(item, effectiveDiscount))
        .toArray(),
    [lineItems, effectiveDiscount],
  );
  const amount = useMemo(
    () => collect(discountedLineItems).reduce((carry, item) => carry + item.amount, 0),
    [discountedLineItems],
  );
  const tax = useMemo(
    () =>
      collect(discountedLineItems)
        .where('tax_behaviour', TAX_BEHAVIOUR.exclusive)
        .reduce((carry, item) => carry + item.tax, 0),
    [discountedLineItems],
  );
  const total = useMemo(
    () => collect(discountedLineItems).reduce((carry, item) => carry + item.taxed_amount, 0),
    [discountedLineItems],
  );

  const isPlanSelected = useMemo(
    () =>
      !!collect(lineItems).firstWhere('product.type', SubscriptionProductType.Plan) ||
      !!collect(lineItems).firstWhere('product.type', SubscriptionProductType.LegacyPlan),
    [lineItems],
  );
  const isGoogleAdsPlanSelected = useMemo(
    () => !!collect(lineItems).firstWhere('product.type', SubscriptionProductType.GoogleAdsPlan),
    [lineItems],
  );

  const isDraft = !data || isEdit;
  const draft = {
    items: discountedLineItems,
    amount,
    tax,
    taxed_amount: total,
  };
  // A saved subscription whose main product is the mobile-app plan shows its
  // flat fee with the contract discount applied — display only; `lineItems`
  // (and thus the payload saveSubscription sends) keep the undiscounted values.
  const displayData = useMemo(
    () => (data && isMobileAppMainSubscription(data) ? discountSubscription(data, effectiveDiscount) : data),
    [data, effectiveDiscount],
  );
  const subscription = displayData ?? draft;

  const updateProduct = (id, data) =>
    setPersonalProducts((current) =>
      collect(current)
        .map((item) =>
          item.id !== id
            ? item
            : {
                ...item,
                ...data,
              },
        )
        .toArray(),
    );
  const getUnitAmount = (price) => (price.tiers.length > 0 ? price.tiers[0].amount : price.amount);
  const addItem = (item) =>
    setLineItems((current) => {
      const taxCalculator = TaxCalculator.make(item.price.tax_behaviour);

      const lineItem = collect(current).firstWhere('product_id', item.id);

      const price = lineItem?.price ?? item?.price;
      const quantity = (lineItem?.quantity ?? 0) + 1;
      const discount = 0;
      const unitAmount = getUnitAmount(price);
      const amount = (unitAmount - discount) * quantity;

      return collect(current)
        .keyBy('product_id')
        .put(item.id, {
          ...item,
          product_id: item.id,
          quantity,
          product: item,
          price,
          amount,
          tax: taxCalculator.tax(amount, price.tax_percentage),
          taxed_amount: taxCalculator.brutto(amount, price.tax_percentage),
          tax_behaviour: price.tax_behaviour,
          discount: discount * quantity,
        })
        .values()
        .toArray();
    });
  const removeItem = (item) =>
    setLineItems((current) => {
      const taxCalculator = TaxCalculator.make(item.price.tax_behaviour);

      const lineItem = collect(current).firstWhere('product_id', item.id);

      const price = lineItem?.price ?? item?.price;
      const quantity = (lineItem?.quantity ?? 0) - 1;
      const discount = 0;
      const unitAmount = getUnitAmount(price);
      const amount = (unitAmount - discount) * quantity;

      return collect(current)
        .keyBy('product_id')
        .put(item.id, {
          ...item,
          product_id: item.id,
          quantity,
          product: item,
          price,
          amount,
          tax: taxCalculator.tax(amount, price.tax_percentage),
          taxed_amount: taxCalculator.brutto(amount, price.tax_percentage),
          tax_behaviour: price.tax_behaviour,
          discount: discount * quantity,
        })
        .values()
        .where('quantity', '>', 0)
        .toArray();
    });
  const updateItem = (item, data) =>
    setLineItems((current) => {
      const taxCalculator = TaxCalculator.make(data.price.tax_behaviour);

      const lineItem = collect(current).firstWhere('product_id', item.id);
      updateProduct(item.id, {
        ...data,
        price_data: data.price.id ? undefined : data.price,
      });

      if (!lineItem) {
        return current;
      }

      const { price } = data;
      const discount = 0;
      const unitAmount = getUnitAmount(price);
      const amount = (unitAmount - discount) * lineItem.quantity;

      return collect(current)
        .keyBy('product_id')
        .put(item.id, {
          product_id: item.id,
          quantity: lineItem.quantity,
          product: item,
          price,
          amount,
          tax: taxCalculator.tax(amount, price.tax_percentage),
          taxed_amount: taxCalculator.brutto(amount, price.tax_percentage),
          tax_behaviour: data.price.tax_behaviour,
          discount: discount * lineItem.quantity,
          price_id: data.price.id,
          price_data: data.price.id ? undefined : data.price,
        })
        .values()
        .toArray();
    });

  const saveSubscription = async () => {
    const _data = {
      billing_mode: billingMode,
      trial_days: hasTrial ? trialDays : null,
      items: lineItems,
    };
    const promise = data
      ? updateSubscription({ data: _data, apiManager: SubscriptionsManager, id: data.id })
      : createSubscription({ data: _data, apiManager: SubscriptionsManager });

    const { error } = await promise;

    if (error) {
      return toast.error(t('toasts.failed_to_create_subscription'));
    }

    onSave && onSave();
    await CurrentCompanyManager.get();
    setIsEdit(false);
    moveForward && moveForward();
    ref.current?.scrollTo();
    toast.success(isEdit ? t('toasts.subscription_updated') : t('toasts.subscription_created'));
  };

  if (!hasPaymentOption) {
    return <PaymentInfoStep />;
  }

  if (step === steps.select_products) {
    return (
      <FadeIn>
        {legacyPlans.length > 0 && (
          <div className="mt-md flex justify-center">
            <Segmented
              value={activePlanTab}
              options={Object.values(PlanTab).map((item) => ({
                value: item,
                label: t(`sections.plans.tabs.${item}.name`),
              }))}
              onChange={(value) => setActivePlanTab(value)}
              className="p-[4px]"
            />
          </div>
        )}
        <h3 className="mt-md w-full text-center">{t(`sections.plans.tabs.${activePlanTab}.title`)}</h3>
        <div className="grid gap-8 mt-lg">
          {(activePlanTab === PlanTab.actual ? plans : legacyPlans).map((item) => {
            const lineItem = collect(lineItems).firstWhere('product_id', item.id);

            return (
              <SubscriptionProduct
                data={item}
                lineItem={lineItem}
                prevState={collect(initialPersonalProducts).firstWhere('id', item.id)}
                disabled={isGoogleAdsPlanSelected}
                discount={effectiveDiscount}
                onAdd={() => addItem(item)}
                onUpdate={(data) => updateItem(item, data)}
                onRemove={() => removeItem(item)}
                key={`plan-${item.id}`}
              />
            );
          })}
        </div>
        {otherProducts.length > 0 && (
          <div className="my-md">
            <Tag className="text-uppercase">{t('labels.recommended')}</Tag>
            <h5 className="mt-md">{t('sections.add_ons.title')}</h5>
            <p className="mt-md">{t('sections.add_ons.description')}</p>
            <div className="grid gap-8 mt-md">
              {otherProducts.map((item) => {
                const lineItem = collect(lineItems).firstWhere('product_id', item.id);

                return (
                  <SubscriptionProduct
                    data={item}
                    lineItem={lineItem}
                    disabled={item.type === SubscriptionProductType.GoogleAdsPlan && isPlanSelected}
                    discount={effectiveDiscount}
                    prevState={collect(initialPersonalProducts).firstWhere('id', item.id)}
                    onAdd={() => addItem(item)}
                    onUpdate={(data) => updateItem(item, data)}
                    onRemove={() => removeItem(item)}
                    variant="secondary"
                    key={`product-${item.id}`}
                  />
                );
              })}
            </div>
          </div>
        )}
        {lineItems?.length > 0 && (
          <Flex className="mt-md" justify="space-between">
            <h6 className="my-auto">
              <Trans t={t} values={{ amount: formatCurrency(amount) }}>
                labels.basket
              </Trans>
            </h6>
            <Flex gap="8px">
              <Button type="primary" onClick={() => setStep(steps.subscribe)}>
                {t('buttons.review_and_subscribe')}
              </Button>
            </Flex>
          </Flex>
        )}
      </FadeIn>
    );
  }

  return (
    <FadeIn ref={ref}>
      <Subscription
        data={isDraft ? draft : subscription}
        isDraft={isDraft}
        billingMode={billingMode}
        setBillingMode={setBillingMode}
        hasTrial={hasTrial}
        setHasTrial={setHasTrial}
        trialDays={trialDays}
        setTrialDays={setTrialDays}
        onEdit={onEdit}
        onSave={onSave}
      />
      {isDraft && (
        <>
          <hr className="my-md" />
          <Agreement
            subscription={draft}
            trialDays={hasTrial ? trialDays : 5}
            agreementAccepted={agreementAccepted}
            setAgreementAccepted={setAgreementAccepted}
            className="mt-md"
          />
          {lineItems?.length > 0 && (
            <Flex justify="space-between" className="mt-md">
              <h6 className="my-auto">
                <Trans t={t} values={{ amount: formatCurrency(total) }}>
                  labels.basket
                </Trans>
              </h6>
              <Flex justify="end" gap="8px">
                <Button onClick={() => setStep(steps.select_products)}>{t('buttons.basket')}</Button>
                <Button
                  type="primary"
                  onClick={saveSubscription}
                  disabled={!agreementAccepted}
                  loading={isCreateLoading || isUpdateLoading}
                >
                  {isEdit ? t('buttons.update_subscription') : t('buttons.subscribe')}
                </Button>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </FadeIn>
  );
}
